import React, { useMemo } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    ReferenceLine,
    Cell,
} from "recharts";
import { format } from "date-fns";
import { es } from "date-fns/locale";

// Formato moneda (mismo criterio que las fichas de costo: 3 decimales)
const fmtMoneda = new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
});

const COLOR_BAR = "#6366f1"; // Indigo
const COLOR_PROM = "#f59e0b"; // Ambar (linea de promedio)

// 'YYYY-MM-DD' -> timestamp LOCAL (evita brincos por UTC)
function toLocalTs(s) {
    if (!s) return null;
    const d10 = String(s).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(d10)) {
        const [y, m, d] = d10.split("-").map(Number);
        return new Date(y, m - 1, d).getTime();
    }
    const t = new Date(s).getTime();
    return isNaN(t) ? null : t;
}

// Suma el costo total del rango dividido por dias => costo promedio por dia.
// Recibe detalleData (una fila por dia/cliente, igual que GraficaConteoLlamadas).
export default function GraficaCostoPromedioDia({ detalleData }) {
    const { data, promedio } = useMemo(() => {
        if (!Array.isArray(detalleData) || !detalleData.length) {
            return { data: [], promedio: 0 };
        }

        const porDia = new Map();
        for (const row of detalleData) {
            const dia = String(row.fecha || row.Fecha || "").slice(0, 10);
            if (!dia) continue;

            const costo =
                Number(row.costo_fijo || 0) +
                Number(row.costo_movil || 0) +
                Number(row.costo_pais || 0) +
                Number(row.costo_buzon || 0);

            const prev = porDia.get(dia) || { dia, ts: toLocalTs(dia), costo: 0 };
            prev.costo += costo;
            porDia.set(dia, prev);
        }

        const arr = [...porDia.values()]
            .filter((d) => d.ts != null)
            .sort((a, b) => a.ts - b.ts)
            .map((d) => ({ ...d, costo: Number(d.costo.toFixed(3)) }));

        const suma = arr.reduce((a, d) => a + d.costo, 0);
        const prom = arr.length ? suma / arr.length : 0;

        return { data: arr, promedio: Number(prom.toFixed(3)) };
    }, [detalleData]);

    if (!data.length) {
        return (
            <div className="h-[280px] w-full bg-white shadow rounded-xl p-3 flex items-center justify-center">
                <div className="text-center">
                    <svg
                        className="w-12 h-12 mx-auto text-gray-300 mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                    </svg>
                    <p className="text-xs font-semibold text-gray-600">
                        Sin datos de costo por día
                    </p>
                    <p className="text-[11px] text-gray-500 mt-1">
                        Selecciona un rango con datos
                    </p>
                </div>
            </div>
        );
    }

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;
        const p = payload[0].payload;
        return (
            <div className="bg-white p-2 shadow-lg rounded-lg border border-gray-200">
                <p className="text-[11px] font-semibold text-gray-700 mb-1">
                    {format(new Date(p.ts), "d 'de' MMMM, yyyy", { locale: es })}
                </p>
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[10px] text-gray-600">Costo del día:</span>
                    <span className="text-[10px] font-semibold text-gray-900">
                        {fmtMoneda.format(p.costo)}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[10px]" style={{ color: COLOR_PROM }}>
                        Promedio del rango:
                    </span>
                    <span
                        className="text-[10px] font-semibold"
                        style={{ color: COLOR_PROM }}
                    >
                        {fmtMoneda.format(promedio)}
                    </span>
                </div>
            </div>
        );
    };

    const isSingle = data.length === 1;

    return (
        <div className="h-[280px] w-full bg-white shadow rounded-xl p-3">
            <div className="text-center mb-2">
                <p className="text-xs font-semibold text-gray-800">Costo por día</p>
                <p className="text-[10px] text-gray-500">
                    Promedio del rango:{" "}
                    <span className="font-semibold" style={{ color: COLOR_PROM }}>
                        {fmtMoneda.format(promedio)}
                    </span>
                </p>
            </div>

            <ResponsiveContainer width="100%" height="87%">
                <BarChart
                    data={data}
                    margin={{ top: 5, right: 10, bottom: 25, left: 0 }}
                    barCategoryGap={isSingle ? "40%" : "20%"}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />

                    <XAxis
                        dataKey="ts"
                        tickMargin={6}
                        tick={{ fontSize: 10, fill: "#6b7280" }}
                        tickFormatter={(ts) => format(new Date(ts), "d MMM", { locale: es })}
                    />

                    <YAxis
                        tick={{ fontSize: 10, fill: "#6b7280" }}
                        tickFormatter={(v) =>
                            v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`
                        }
                        domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]}
                    />

                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: "rgba(99,102,241,0.06)" }}
                    />

                    {/* Linea de promedio del rango */}
                    <ReferenceLine
                        y={promedio}
                        stroke={COLOR_PROM}
                        strokeDasharray="4 4"
                        strokeWidth={1.5}
                    />

                    <Bar
                        dataKey="costo"
                        name="Costo"
                        radius={[4, 4, 0, 0]}
                        isAnimationActive
                        animationDuration={700}
                        maxBarSize={48}
                    >
                        {data.map((_, i) => (
                            <Cell key={i} fill={COLOR_BAR} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}